import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const validationPatternsCode = `// 1. Structural Validation: The RFC-compliant Email Pattern (Simplified)
const emailRegex = /^[a-zA-Z0-9.!#\$\%&'*+/=?^_\`{|}~-]+@[a-zA-Z0-9-]+(?:\\.[a-zA-Z0-9-]+)*$/;

// 2. Strong Password Validation
// Must contain: 8+ chars, 1 uppercase, 1 lowercase, 1 digit, 1 special char
const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\\d)(?=.*[@\$!\%*?&])[A-Za-z\\d@\$!\%*?&]{8,}$/;

// 3. Robust URL Parsing
const urlRegex = /^(?<protocol>https?):\\/\\/(?<domain>[\\w.-]+)(?<path>\\/[\\w.-]*)*$/;
const match = "https://example.com/api/v1".match(urlRegex);
console.log(match.groups.domain); // "example.com"`;

const sanitizationCode = `// XSS Prevention: Escaping HTML Entities
function escapeHTML(str) {
    const map = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
    };
    return str.replace(/[&<>"']/g, m => map[m]);
}

// Slugification: Converting titles for URLs
function slugify(title) {
    return title
        .toLowerCase()
        .trim()
        .replace(/[^\\w\\s-]/g, '') // Remove non-word chars
        .replace(/[\\s_-]+/g, '-')   // Replace spaces/underscores with hyphens
        .replace(/^-+|-+$/g, '');   // Trim hyphens from ends
}`;

const parsingExtractionCode = `// Thousands Separator (Lookahead Pattern)
function formatCurrency(amount) {
    return amount.toString().replace(/\\B(?=(\\d{3})+(?!\\d))/g, ",");
}

console.log(formatCurrency(1234567.89)); // "1,234,567.89"

// Extracting Hex Colors from CSS
const css = "body { color: #fff; background: #00ff00; }";
const hexRegex = /#([a-f0-9]{3}|[a-f0-9]{6})\\b/gi;
console.log(css.match(hexRegex)); // ["#fff", "#00ff00"]`;

const JSRegexPractical = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Implementation</div>
                <h1>Practical Regex Engineering</h1>
                <p className="hero-lead">
                    regex is the "Swiss Army Knife" of text processing. Move from theory
                    to implementation by learning how to sanitize user input, parse
                    complex data structures, and automate text transformations in production.
                </p>
            </div>

            <div className="content-section">
                <h2>Production Validation</h2>
                <p>
                    Validation is the first line of defense in application security.
                    Whether it's ensuring a user provides a strong password or
                    validating an email format before a database write, well-crafted
                    regex patterns prevent "garbage in, garbage out" scenarios.
                </p>
                <CodeBlock code={validationPatternsCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Sanitization & Security</h2>
                <p>
                    Regex is critical for preventing **Cross-Site Scripting (XSS)**
                    by escaping dangerous HTML characters. It's also the standard tool
                    for generating "slugs" (URL-friendly versions of titles), where
                    you must selectively remove special characters and normalize
                    whitespace to avoid broken links or injection vulnerabilities.
                </p>
                <CodeBlock code={sanitizationCode} language="javascript" />
            </div>

            <div className="content-section">
                <h2>Data Transformation</h2>
                <p>
                    Regex exceles at "find and replace" operations that require
                    context. Using lookaheads for numeric formatting (like adding
                    thousands separators) or global matches for extracting specific
                    data (like hex codes from a stylesheet) saves hundreds of lines
                    of manual string looping logic.
                </p>
                <CodeBlock code={parsingExtractionCode} language="javascript" />
            </div>

            <ProTip><p>
                    While regex is powerful, it can be computationally expensive.
                    Avoid "Catastrophic Backtracking" caused by nested quantifiers (e.g.,
                    `(a+)+$`). For high-throughput applications, consider using
                    the `Intl.NumberFormat` API for currency instead of regex, and
                    always prefer native browser APIs for URL parsing (`new URL()`)
                    unless you are working in an environment where they are unavailable.
                </p></ProTip>

            <KeyFacts><ul>
                    <li>**Security:** Always escape HTML entities in user-provided text before rendering.</li>
                    <li>**Formatting:** Use the "Thousands Separator" pattern for readable numeric displays.</li>
                    <li>**Validation:** Combine length checks with regex for comprehensive password security.</li>
                    <li>**Extraction:** Use the global `g` flag for batch data mining from large text blocks.</li>
                    <li>**Optimization:** Replace complex regex with native APIs (like `Intl` or `URL`) where possible for better performance.</li>
                </ul></KeyFacts>

        </div>
    );
};

export default JSRegexPractical;


